// apps/client/src/ui/App.tsx
import type { Phase } from '../state/slices/core';
import { needsLandscape, titleShowing } from '../state/slices/title';
import { useGame } from '../state/store';
import { FullscreenButton, FullscreenGuide } from './fullscreen';
import { Scrim, Toasts } from './kit';
import { RotateCard, usePortrait } from './rotate';
import { baseScreen, getScreen, type ScreenDef } from './router';

/** A panel layer under another panel stays mounted (its state survives) but hidden, so its tab never peeks out behind the top one.
 *  In town (`in-town`), the shop panels sit below the top HUD row, so the bells stay in sight (tokens.css). A full screen under another
 *  full screen is hidden the same way (title screen, 2026-10-05: over the 3D backdrop their pages are see-through). */
function Layer({ def, params, close, covered = false, inTown = false }: { def: ScreenDef; params: unknown; close: () => void; covered?: boolean; inTown?: boolean }) {
  const C = def.component as (p: { params: unknown; close: () => void }) => React.ReactElement | null;
  if (def.layer === 'panel') {
    return <div className={`layer-panel${inTown ? ' in-town' : ''}`} hidden={covered}><Scrim onClick={close} /><C params={params} close={close} /></div>;
  }
  if (def.layer === 'full') return <div className="layer-hold" hidden={covered}><C params={params} close={close} /></div>;
  return <C params={params} close={close} />;
}
/** The screens before a town (sign-in, the creator, the towns list, the invite landing): Full screen sits in their top right corner,
 *  but not over a panel or a task opened over them (found a town, edit your character), which have the screen to themselves. */
const BEFORE_TOWN: ReadonlySet<Phase> = new Set(['signedOut', 'needsProfile', 'towns']);
export function App() {
  const phase = useGame((s) => s.phase);
  const titleStep = useGame((s) => s.titleStep);
  const stack = useGame((s) => s.screens);
  const closeScreen = useGame((s) => s.closeScreen);
  const portrait = usePortrait();
  const base = baseScreen(titleStep, phase, window.location.pathname);   // the title first on every launch (2026-10-05)
  const baseDef = getScreen(base.id) ?? getScreen('boot');
  const topPanel = stack.findLastIndex((s) => getScreen(s.id)?.layer === 'panel');
  const topFull = stack.findLastIndex((s) => getScreen(s.id)?.layer === 'full');
  const inTown = phase === 'inTown';
  // Portrait landing (2026-10-18): a phone held upright gets the landing page; from Play now on, the rotate card over the scene.
  const rotate = portrait && needsLandscape(titleStep, phase);
  const corner = !titleShowing(titleStep, phase) && BEFORE_TOWN.has(phase) && topPanel < 0 && topFull < 0;
  return (
    <>
      <div className="app-screens" data-rotated={rotate || undefined}>
        {baseDef && <Layer def={baseDef} params={baseDef.id === base.id ? base.params : undefined} close={() => {}} covered={topFull >= 0} />}
        {stack.map((s, i) => {
          const def = getScreen(s.id);
          const covered = (def?.layer === 'panel' && i < topPanel) || (def?.layer === 'full' && i < topFull);
          return def ? <Layer key={`${s.id}-${i}`} def={def} params={s.params} close={closeScreen} covered={covered} inTown={inTown} /> : null;
        })}
        {corner && <div className="fs-corner"><FullscreenButton /></div>}
        <Toasts />
      </div>
      {rotate && <RotateCard />}
      <FullscreenGuide />
    </>
  );
}
